import { useImperativeHandle, useRef, useState, type Ref } from 'react';
import { useTranslation } from 'react-i18next';

import { DataPanel, DataPanelItem, NgButton, NgModal } from '@/ui';
import type { NgModalHandle } from '@/ui';
import type { IncidentInvolvedPsapStateComponentInfo } from '../subpanels/involvedPsapsInfo';
import { InvolvedPsapCollaborationStateBadge } from '../involved-psaps/InvolvedPsapCollaborationStateBadge';

export type InvolvedPsapsDetailDialogHandle = {
  show: (incidentInvolvedPsapStates: IncidentInvolvedPsapStateComponentInfo[]) => void;
};

export type InvolvedPsapsDetailDialogProps = {
  ref?: Ref<InvolvedPsapsDetailDialogHandle>;
};

function InvolvedPsapsDetailDialogItem({ involvedPsap }: { involvedPsap: IncidentInvolvedPsapStateComponentInfo }) {
  const orderedHistory = [...involvedPsap.statusHistory].sort(
    (a, b) => Date.parse(a.timestampUtc) - Date.parse(b.timestampUtc),
  );

  return (
    <div className="list-group-item px-0">
      <div className="d-flex justify-content-between align-items-center gap-2">{involvedPsap.psapName}</div>
      <div className="col">
        {orderedHistory.map((record) => (
          <InvolvedPsapCollaborationStateBadge key={`${record.timestampUtc}-${record.state}`} currentState={record} />
        ))}
      </div>
    </div>
  );
}

export function InvolvedPsapsDetailDialog({ ref }: InvolvedPsapsDetailDialogProps) {
  const { t } = useTranslation();
  const modalRef = useRef<NgModalHandle>(null);
  const [psapStates, setPsapStates] = useState<IncidentInvolvedPsapStateComponentInfo[]>([]);

  useImperativeHandle(ref, () => ({
    show: (incidentInvolvedPsapStates) => {
      setPsapStates(incidentInvolvedPsapStates);
      modalRef.current?.show();
    },
  }));

  return (
    <NgModal
      ref={modalRef}
      header={t('Incident_InvolvedPsaps_DialogTitle')}
      bodyTemplate={
        <DataPanel>
          <DataPanelItem>
            <div className="list-group list-group-flush">
              {psapStates.map((item) => (
                <InvolvedPsapsDetailDialogItem key={item.psapId} involvedPsap={item} />
              ))}
            </div>
          </DataPanelItem>
        </DataPanel>
      }
      footerTemplate={<NgButton text={t('Button_Close')} onClick={() => modalRef.current?.hide()} />}
    />
  );
}
